import { Module } from 'vuex';
import { RootState, RootGettersReturns } from '../types/index';
import {
  ElementGettersKeys,
  ElementMutationsKeys,
  ElementMutationsPayload,
  ElementActionsKeys,
  ElementState,
  ElementActionsPayload,
} from '../types/element';
import API from '@/plugins/axios';
import { CreateElementEmit, SocketActionsKeys, elementResponse, UpdateElementEmit } from '../types/socket';
import Axios from 'axios';
import { v4 as UUIDv4 } from 'uuid';
import Vue from 'vue';

const INIT_Z_INDEX = 500000000;
const ADD_Z_INDEX = 10000;

const initState = () => {
  const state: ElementState = {
    elements: [],
    selectedElement: null,
    isDoubleSelected: false,
    manipulatingElement: false,
    undoStack: [],
    redoStack: [],
    progressBars: {},
  };
  return state;
};
const Element: Module<ElementState, RootState> = {
  state: {
    ...initState(),
  },
  actions: {
    [ElementActionsKeys.LOAD_ELEMENTS]: async ({ commit, rootGetters }) => {
      const boardId = (rootGetters as RootGettersReturns).boardId;
      const resp = await API.get(`/api/board/${boardId}/elements`);
      commit(ElementMutationsKeys.ELEMENT_RESET_STATE);
      commit(ElementMutationsKeys['SET_ELEMENTS'], resp.data.elements);
    },
    [ElementActionsKeys.ADD_ELEMENT]: async (
      { commit, dispatch, rootGetters },
      payload: ElementActionsPayload['ADD_ELEMENT']
    ) => {
      if ((rootGetters as RootGettersReturns).sharedBoard) return;
      const boardId = (rootGetters as RootGettersReturns).boardId;
      const params: CreateElementEmit = {
        type: 'create_element',
        params: [
          {
            board_element: {
              ...payload,
              data: { ...payload.data, zIndex: (rootGetters as RootGettersReturns).biggestZIndex + ADD_Z_INDEX },
            },
            board_id: boardId,
          },
        ],
      };
      const element = (await dispatch(SocketActionsKeys.SOCKET_EMIT, params)) as elementResponse;
      commit(ElementMutationsKeys.ADD_ELEMENT, element.board_element);
      commit(ElementMutationsKeys.ADD_UNDO, { type: 'ADD_ELEMENT', element: element.board_element });
      return element.board_element;
    },
    [ElementActionsKeys.UPDATE_ELEMENT]: async (
      { commit, dispatch, rootGetters },
      payload: ElementActionsPayload['UPDATE_ELEMENT']
    ) => {
      const sharedBoard = (rootGetters as RootGettersReturns).sharedBoard;
      if (sharedBoard) {
        const currentElement = Array.isArray(payload) ? payload[0] : payload;
        const existingElement = (rootGetters as RootGettersReturns).allElements.find(el => el.id === currentElement.id);
        if (
          ['Office', 'Pdf'].includes(currentElement.type) &&
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (currentElement.data as any).currentPage !== (existingElement?.data as any).currentPage
        ) {
          commit(ElementMutationsKeys.UPDATE_ELEMENT, currentElement);
        }
        return;
      }
      const currentElement = Array.isArray(payload) ? payload[0] : payload;
      const boardId = (rootGetters as RootGettersReturns).boardId;
      const existingElement = (rootGetters as RootGettersReturns).allElements.find(el => el.id === currentElement.id);
      const params: UpdateElementEmit = {
        type: 'update_element',
        params: [{ board_element: currentElement, board_id: boardId, save: true }],
      };
      const element = (await dispatch(SocketActionsKeys.SOCKET_EMIT, params)) as elementResponse;
      commit(ElementMutationsKeys.UPDATE_ELEMENT, element.board_element);
      if (!Array.isArray(payload)) {
        commit(ElementMutationsKeys.ADD_UNDO, { type: 'UPDATE_ELEMENT', element: existingElement });
      }
    },
    [ElementActionsKeys.SELECT_ELEMENT]: (
      { commit, dispatch, rootGetters },
      payload: ElementActionsPayload['SELECT_ELEMENT']
    ) => {
      if (payload) {
        dispatch(ElementActionsKeys.UPDATE_ELEMENT, {
          ...payload,
          data: {
            ...payload.data,
            zIndex: (rootGetters as RootGettersReturns).biggestZIndex + ADD_Z_INDEX,
          },
        });
      }
      commit(ElementMutationsKeys.SELECT_ELEMENT, payload);
    },
    [ElementActionsKeys.UNDO]: ({ state, commit, dispatch, rootGetters }) => {
      if ((rootGetters as RootGettersReturns).sharedBoard) return;
      if (!state.undoStack.length) return;
      const lastUndo = state.undoStack[state.undoStack.length - 1];
      const existingElement = (rootGetters as RootGettersReturns).allElements.find(el => el.id === lastUndo.element.id);
      dispatch(ElementActionsKeys.UPDATE_ELEMENT, [
        { ...lastUndo.element, deleted: lastUndo.type === 'ADD_ELEMENT' ? true : lastUndo.element.deleted },
        true,
      ]);
      commit(ElementMutationsKeys.ADD_REDO, existingElement);
    },
    [ElementActionsKeys.REDO]: ({ state, commit, dispatch, rootGetters }) => {
      if ((rootGetters as RootGettersReturns).sharedBoard) return;
      if (!state.redoStack.length) return;
      const lastRedo = state.redoStack[state.redoStack.length - 1];
      const existingElement = (rootGetters as RootGettersReturns).allElements.find(el => el.id === lastRedo.id);
      dispatch(ElementActionsKeys.UPDATE_ELEMENT, [{ ...lastRedo }, true]);
      commit(ElementMutationsKeys.ADD_UNDO, { type: 'UPDATE_ELEMENT', element: { ...existingElement }, redo: true });
    },
    [ElementActionsKeys.SET_DOUBLE_SELECTED]: ({ commit }, payload: ElementActionsPayload['SET_DOUBLE_SELECTED']) => {
      commit(ElementMutationsKeys.SET_DOUBLE_SELECTED, payload);
    },
    [ElementActionsKeys.SET_MANIPULATING_ELEMENT]: (
      { commit },
      payload: ElementActionsPayload['SET_MANIPULATING_ELEMENT']
    ) => {
      commit(ElementMutationsKeys.SET_MANIPULATING_ELEMENT, payload);
    },
    [ElementActionsKeys.UPLOAD_ELEMENT]: async (
      { dispatch, rootGetters },
      payload: ElementActionsPayload['UPLOAD_ELEMENT']
    ) => {
      if ((rootGetters as RootGettersReturns).sharedBoard) return;
      const resp = await API.post('/api/file/upload', {
        filename: payload.file.name,
      });
      if (resp.status !== 200) return;
      const uuid = UUIDv4();
      dispatch(ElementActionsKeys.CREATE_PROGRESS_BAR, { id: uuid });
      const googleResp = await Axios.put(resp.data.signedURL, payload.file, {
        headers: {
          'Content-Type': payload.file.type,
        },
        onUploadProgress: progressEvent => {
          console.log(progressEvent);
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch(ElementActionsKeys.UPDATE_PROGRESS_BAR, { id: uuid, progress: percentCompleted });
        },
      });
      dispatch(ElementActionsKeys.DELETE_PROGRESS_BAR, { id: uuid });
      if (googleResp.status !== 200) return;
      const data = {
        ...payload.element,
        data: { ...payload.element.data, uuid: resp.data.fileUuid, filename: resp.data.realFilename },
      };
      let element;
      if (!payload.changeEl) {
        element = await dispatch(ElementActionsKeys.ADD_ELEMENT, data);
      } else {
        element = await dispatch(ElementActionsKeys.UPDATE_ELEMENT, data);
      }
      return element;
    },
    [ElementActionsKeys.CREATE_PROGRESS_BAR]: async (
      { commit },
      payload: ElementActionsPayload['CREATE_PROGRESS_BAR']
    ) => {
      commit(ElementMutationsKeys.CREATE_PROGRESS_BAR, payload);
    },
    [ElementActionsKeys.UPDATE_PROGRESS_BAR]: async (
      { commit },
      payload: ElementActionsPayload['UPDATE_PROGRESS_BAR']
    ) => {
      commit(ElementMutationsKeys.UPDATE_PROGRESS_BAR, payload);
    },
    [ElementActionsKeys.DELETE_PROGRESS_BAR]: async (
      { commit },
      payload: ElementActionsPayload['DELETE_PROGRESS_BAR']
    ) => {
      commit(ElementMutationsKeys.DELETE_PROGRESS_BAR, payload);
    },
    [ElementActionsKeys.SOCKET_ON_CREATED_ELEMENT]: async (
      { commit },
      payload: ElementActionsPayload['SOCKET_ON_CREATED_ELEMENT']
    ) => {
      commit(ElementMutationsKeys.ADD_ELEMENT, payload.element);
    },
    [ElementActionsKeys.SOCKET_ON_UPDATED_ELEMENT]: async (
      { commit },
      payload: ElementActionsPayload['SOCKET_ON_UPDATED_ELEMENT']
    ) => {
      commit(ElementMutationsKeys.UPDATE_ELEMENT, payload.element);
    },
    [ElementActionsKeys.RESET_STATE]: ({ commit }) => {
      commit(ElementMutationsKeys.ELEMENT_RESET_STATE);
    },
  },
  mutations: {
    [ElementMutationsKeys.SET_ELEMENTS]: (state, payload: ElementMutationsPayload['SET_ELEMENTS']) => {
      state.elements = payload;
    },
    [ElementMutationsKeys.SELECT_ELEMENT]: (state, payload: ElementMutationsPayload['SELECT_ELEMENT']) => {
      state.selectedElement = payload;
    },
    [ElementMutationsKeys.SET_DOUBLE_SELECTED]: (state, payload: ElementMutationsPayload['SET_DOUBLE_SELECTED']) => {
      state.isDoubleSelected = payload;
    },
    [ElementMutationsKeys.SET_MANIPULATING_ELEMENT]: (
      state,
      payload: ElementMutationsPayload['SET_MANIPULATING_ELEMENT']
    ) => {
      state.manipulatingElement = payload;
    },
    [ElementMutationsKeys.ADD_ELEMENT]: (state, payload: ElementMutationsPayload['ADD_ELEMENT']) => {
      state.elements = [...state.elements, payload];
    },
    [ElementMutationsKeys.UPDATE_ELEMENT]: (state, payload: ElementMutationsPayload['UPDATE_ELEMENT']) => {
      state.elements = [...state.elements.filter(el => el.id !== payload.id), payload];
      if (state.selectedElement && state.selectedElement.id === payload.id) {
        if (payload.deleted) {
          state.selectedElement = null;
        } else {
          state.selectedElement = payload;
        }
      }
    },
    [ElementMutationsKeys.ADD_UNDO]: (state, payload: ElementMutationsPayload['ADD_UNDO']) => {
      const newPayload = { type: payload.type, element: payload.element };
      if (payload.redo) {
        state.redoStack.pop();
      } else {
        state.redoStack = [];
      }
      state.undoStack.push(newPayload);
    },
    [ElementMutationsKeys.ADD_REDO]: (state, payload: ElementMutationsPayload['ADD_REDO']) => {
      state.undoStack.pop();
      state.redoStack.push({ ...payload });
    },
    [ElementMutationsKeys.CREATE_PROGRESS_BAR]: (state, payload: ElementMutationsPayload['CREATE_PROGRESS_BAR']) => {
      Vue.set(state.progressBars, payload.id, 0);
      console.log('state.progressBars[payload.id] = 0;', state.progressBars[payload.id]);
    },
    [ElementMutationsKeys.UPDATE_PROGRESS_BAR]: (state, payload: ElementMutationsPayload['UPDATE_PROGRESS_BAR']) => {
      Vue.set(state.progressBars, payload.id, payload.progress);
      console.log('state.progressBars[payload.id] = payload.progress;', state.progressBars[payload.id]);
    },
    [ElementMutationsKeys.DELETE_PROGRESS_BAR]: (state, payload: ElementMutationsPayload['DELETE_PROGRESS_BAR']) => {
      delete state.progressBars[payload.id];
      console.log('delete state.progressBars[payload.id];');
      // Object.assign(state, { ...state });
      Vue.set(state, 'progressBars', { ...state.progressBars });
    },
    [ElementMutationsKeys.ELEMENT_RESET_STATE]: state => {
      Object.assign(state, initState());
    },
  },
  getters: {
    [ElementGettersKeys.boardElements]: state =>
      state.elements.filter(el => !el.deleted).sort((a, b) => Number(a.id) - Number(b.id)),
    [ElementGettersKeys.allElements]: state => state.elements,
    [ElementGettersKeys.selectedElement]: state => state.selectedElement,
    [ElementGettersKeys.isDoubleSelected]: state => state.isDoubleSelected,
    [ElementGettersKeys.isManipulatingElement]: state => state.manipulatingElement,
    [ElementGettersKeys.biggestZIndex]: state =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (state.elements.sort((a, b) => (b.data as any).zIndex - (a.data as any).zIndex)[0]?.data as any)?.zIndex ||
      INIT_Z_INDEX,
    [ElementGettersKeys.smallestZIndex]: state =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (state.elements.sort((a, b) => (a.data as any).zIndex - (b.data as any).zIndex)[0]?.data as any)?.zIndex ||
      INIT_Z_INDEX,
    [ElementGettersKeys.haveUndo]: state => !!state.undoStack.length,
    [ElementGettersKeys.haveRedo]: state => !!state.redoStack.length,
    [ElementGettersKeys.progressBars]: state => {
      return { ...state.progressBars };
    },
  },
};

export default Element;
